/**
 * Root layout — Vite/React Router edition.
 *
 * The original Next.js root layout owned the `<html>` and `<body>` tags.
 * In the Vite SPA build those live in `index.html`, so this component only
 * provides the in-app chrome (navigation, providers, footer) and renders the
 * matched route via `<Outlet>`.
 *
 * Font handling: the Next version pulled three families through
 * `next/font/google`. The jest-client loads them statically from Google Fonts
 * via a `<link>` tag in index.html; the CSS variables (`--font-inter`, etc.)
 * are wired up in globals.css instead of at runtime.
 */
import { Outlet } from 'react-router-dom';
import { usePathname } from 'next/navigation';
import { Navigation } from '@/components/layout/Navigation';
import { Footer } from '@/components/layout/Footer';
import {
  BottomTabBar,
  BOTTOM_TAB_BAR_PADDING,
  isBottomTabBarVisible,
} from '@/components/layout/BottomTabBar';
import { Providers } from '@/components/providers/Providers';
import RouteTracker from '@/components/RouteTracker';
import { useJestPurchaseRecovery } from '@/hooks/useJestPurchaseRecovery';

export default function RootLayout() {
  // Runs once as soon as both the JestSDK and an access token are available —
  // drains any incomplete IAP purchases per Jest's startup contract.
  useJestPurchaseRecovery();
  const pathname = usePathname();
  const showTabBar = isBottomTabBarVisible(pathname);

  return (
    <Providers>
      <RouteTracker />
      <Navigation />
      <main className="min-h-[calc(100vh-var(--nav-chrome-min))]">
        <Outlet />
      </main>
      <Footer className={showTabBar ? BOTTOM_TAB_BAR_PADDING : undefined} />
      <BottomTabBar />
    </Providers>
  );
}
